// src/routes/test/BiometricAuthIntegratedTest.js
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import biometricService from 'shared/BiometricService';
import { devLog } from 'utils/logger';

function BiometricAuthIntegratedTest() {
  // 상태 관리
  const [supported, setSupported] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(`user_${Math.floor(Math.random() * 10000)}`);
  const [authResult, setAuthResult] = useState(null);
  const [keyInfo, setKeyInfo] = useState(null);

  // 초기 마운트 시 체크
  useEffect(() => {
    const isSupported = biometricService.isSupported();
    setSupported(isSupported);
    setPlatform(biometricService.getPlatform());
    
    if (isSupported) {
      setStatus(`✅ 생체인증 지원됨 (${biometricService.getPlatform()})`);
    } else {
      setStatus('❌ 생체인증 지원되지 않음');
      setError('이 기기에서는 생체인증을 사용할 수 없습니다.');
    }
  }, []);

  // 등록 핸들러
  const handleRegister = async () => {
    if (!supported) return;
    
    setLoading(true);
    setError('');
    setStatus('🔐 생체인증 처리 중...');
    
    try {
      // 서버에서 챌린지 요청 (선택적)
      // let challengeData;
      // requester.requestBiometricRegisterChallenge({ userId }, (result) => {
      //   if (result.code === 0) {
      //     challengeData = result.data;
      //   }
      // });
      
      // 생체인증 키 등록 
      const result = await biometricService.registerBiometricKey(userId);
      devLog('등록 결과:', result);
      
      // 서버에 공개키 등록 (선택적)
      // requester.registerBiometricKey(result, (response) => {
      //   devLog('서버 등록 결과:', response);
      // });
      
      setKeyInfo(result);
      setRegistered(true);
      setStatus(`✅ 생체인증 키 등록 성공 (${platform})`);
      
      toast.success('생체인증 키가 성공적으로 등록되었습니다!');
    } catch (err) {
      console.error('등록 오류:', err);
      setError(err.message);
      setStatus('❌ 생체인증 키 등록 실패');
      
      toast.error(`등록 실패: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 인증 핸들러
  const handleAuthenticate = async () => {
    if (!supported || !registered) return;
    
    setLoading(true);
    setError('');
    setStatus('🔐 생체인증 처리 중...');
    setAuthResult(null);
    
    try {
      // 서버에서 인증 챌린지 요청 (선택적)
      // let challengeData;
      // requester.requestBiometricLoginChallenge({ userId }, (result) => {
      //   if (result.code === 0) {
      //     challengeData = result.data.challenge;
      //   }
      // });
      
      // 인증용 챌린지 생성 (실제로는 서버에서 받아야 함)
      const challenge = `auth_challenge_${Date.now()}`;
      
      // 생체인증으로 서명
      const result = await biometricService.authenticateAndSignData(userId, challenge);
      devLog('인증 결과:', result);
      
      // 서버에 로그인 요청 (선택적)
      // requester.biometricLogin({
      //   userId,
      //   signature: result.signature,
      //   challenge: result.data
      // }, (response) => {
      //   devLog('서버 로그인 결과:', response);
      // });
      
      setAuthResult(result);
      setStatus(`✅ 생체인증 성공 (${platform})`);
      
      toast.success('생체인증이 성공적으로 완료되었습니다!');
    } catch (err) {
      console.error('인증 오류:', err);
      setError(err.message);
      setStatus('❌ 생체인증 실패');
      
      toast.error(`인증 실패: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
    // 사용자 ID가 변경되면 등록 상태 초기화
    setRegistered(false);
    setKeyInfo(null);
    setAuthResult(null);
  };

  // UI는 기존 BiometricAuthTest와 동일하지만,
  // 플랫폼 정보를 추가로 표시
  return (
    <div style={{ padding: 20, maxWidth: 500, margin: 'auto' }}>
      <h1>생체인증 통합 테스트</h1>
      
      <div style={{ background: '#f5f5f5', padding: 15, borderRadius: 8, marginBottom: 20 }}>
        <p><strong>상태:</strong> {status}</p>
        <p><strong>플랫폼:</strong> {platform}</p>
        <p><strong>지원 여부:</strong> {supported ? '지원함' : '지원 안 함'}</p>
        <p><strong>등록 여부:</strong> {registered ? '등록됨' : '미등록'}</p>
        {error && <p style={{ color: 'red' }}><strong>오류:</strong> {error}</p>}
      </div>

      {/* 나머지 UI 컴포넌트는 기존 BiometricAuthTest와 동일 */}

      <div style={{ marginBottom: 20 }}>
        <h2>사용자 정보</h2>
        <div style={{ marginBottom: 10 }}>
          <label htmlFor="userId">사용자 ID:</label>
          <input
            id="userId"
            type="text"
            value={userId}
            onChange={handleUserIdChange}
            style={{
              width: '100%',
              padding: 8,
              marginTop: 5,
              borderRadius: 4,
              border: '1px solid #ccc'
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <h2>등록</h2>
        <button
          onClick={handleRegister}
          disabled={!supported || loading}
          style={{
            width: '100%',
            padding: 14,
            backgroundColor: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: loading || !supported ? 'not-allowed' : 'pointer',
            opacity: loading || !supported ? 0.6 : 1,
          }}
        >
          {loading ? '처리중...' : '생체인증 키 등록하기'}
        </button>
        
        {keyInfo && (
          <div style={{ marginTop: 10, fontSize: '0.8rem', overflow: 'auto', maxHeight: 150 }}>
            <p><strong>키 정보:</strong></p>
            <pre style={{ background: '#eee', padding: 10, borderRadius: 4 }}>
              {JSON.stringify(keyInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {registered && (
        <div style={{ marginBottom: 20 }}>
          <h2>인증</h2>
          <button
            onClick={handleAuthenticate}
            disabled={!supported || loading || !registered}
            style={{
              width: '100%',
              padding: 14,
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading || !supported || !registered ? 'not-allowed' : 'pointer',
              opacity: loading || !supported || !registered ? 0.6 : 1,
            }}
          >
            {loading ? '처리중...' : '생체인증으로 인증하기'}
          </button>
          
          {authResult && (
            <div style={{ marginTop: 10, fontSize: '0.8rem', overflow: 'auto', maxHeight: 150 }}>
              <p><strong>인증 결과:</strong></p>
              <pre style={{ background: '#eee', padding: 10, borderRadius: 4 }}>
                {JSON.stringify(authResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
    
  );
}

export default BiometricAuthIntegratedTest;